import { sqlQuery } from '@/shared/helpers/api';
import { http } from '@/shared/httpWrapper';

export type Quantity = {
    identity: string;
    name: string;
    description: string | null;
    definition: string | null;
    isBaseQuantity: boolean;
    alternateNames: string | null;
    dimension: string | null;
    coreUoM: string | null;
    remark: string | null;
    iec61360Code: string | null;
    iso15926Url: string | null;
};

export type UnitOfMeasure = {
    identity: string;
    name: string;
    description: string | null;
    definition: string | null;
    quantity: string | null;
    conversion: string | null;
    measureSystem: string | null;
    aliasOfUoM: string | null;
    alternateSymbols: string | null;
    isInBaseUnits: boolean;
    isToBeTranslatedToAlias: boolean;
    isDefaultForTransport: boolean;
    isDefaultForPrint: boolean;
    remark: string | null;
    iec61360Code: string | null;
    dexpiName: string | null;
    iso15926Name: string | null;
    iso15926Url: string | null;
    isCfihos: boolean;
};

export type MeasureSystem = {
    name: string;
    description: string | null;
    remark: string | null;
};

/**
 * A unit with its conversion joined in. The join is on the surrogate keys
 * `UnitOfMeasure.Conversion_ID` and `UnitOfMeasureConversion.Id` rather than on
 * the `Identity` strings, because comparing those hits a collation conflict
 * between the two libraries. The surrogate keys are also exact where the text
 * keys would not be: seven identities differ only by case, `mPa|pressure` and
 * `MPa|pressure` among them, and a case-insensitive match would confuse them.
 */
export type UnitWithConversion = UnitOfMeasure & {
    factorA: number | null;
    factorB: number | null;
    isQualityChecked: boolean;
    hasConversion: boolean;
};

/**
 * A joined row as it comes back from the server. `conversionIsValid` is null
 * when the unit has no conversion, and false when it points at a retired one;
 * neither should contribute a factor.
 */
type UnitRow = UnitOfMeasure & {
    factorA: number | null;
    factorB: number | null;
    isQualityChecked: boolean | null;
    conversionIsValid: boolean | null;
};

export type UomData = {
    quantities: Quantity[];
    units: UnitWithConversion[];
    measureSystems: MeasureSystem[];
};

async function select<T>(query: string): Promise<T[]> {
    return (await sqlQuery(http, { query })).data as T[];
}

/**
 * Loads the whole Unit of Measure data set in one go. It is roughly a thousand
 * rows in total, which keeps every subsequent interaction — selecting a
 * quantity, searching, converting — instant and offline from the server.
 */
export async function loadUomData(): Promise<UomData> {
    const [quantities, unitRows, measureSystems] = await Promise.all([
        select<Quantity>(`
            SELECT [Identity] [identity], [Name] name, [Description] description,
                   [Definition] definition, [IsBaseQuantity] isBaseQuantity,
                   [AlternateNames] alternateNames, [Dimension] dimension,
                   [CoreUoM] coreUoM, [Remark] remark,
                   [IEC61360Code] iec61360Code, [ISO15926URL] iso15926Url
            FROM [Quantity]
            WHERE [IsValid] = 1
            ORDER BY [Name]`),
        // The conversion is an optional attribute, so this has to be a left join,
        // and the conversion's own validity has to be filtered on the joined row
        // rather than in the WHERE clause, which would drop units instead.
        select<UnitRow>(`
            SELECT u.[Identity] [identity], u.[Name] name, u.[Description] description,
                   u.[Definition] definition, u.[Quantity] quantity, u.[Conversion] conversion,
                   u.[MeasureSystem] measureSystem, u.[AliasOfUoM] aliasOfUoM,
                   u.[AlternateSymbols] alternateSymbols, u.[IsInBaseUnits] isInBaseUnits,
                   u.[IsToBeTranslatedToAlias] isToBeTranslatedToAlias,
                   u.[IsDefaultForTransport] isDefaultForTransport,
                   u.[IsDefaultForPrint] isDefaultForPrint, u.[Remark] remark,
                   u.[IEC61360Code] iec61360Code, u.[DEXPIName] dexpiName,
                   u.[ISO15926Name] iso15926Name, u.[ISO15926URL] iso15926Url,
                   u.[IsCFIHOS] isCfihos, c.[FactorA] factorA, c.[FactorB] factorB,
                   c.[IsQualityChecked] isQualityChecked, c.[IsValid] conversionIsValid
            FROM [UnitOfMeasure] u
            LEFT JOIN [UnitOfMeasureConversion] c ON u.[Conversion_ID] = c.[Id]
            WHERE u.[IsValid] = 1
            ORDER BY u.[Name]`),
        select<MeasureSystem>(`
            SELECT [Name] name, [Description] description, [Remark] remark
            FROM [UnitOfMeasureSystem]
            WHERE [IsValid] = 1
            ORDER BY [Name]`)
    ]);

    const units: UnitWithConversion[] = unitRows.map(row => {
        const { conversionIsValid, ...unit } = row;
        const joined = conversionIsValid === true;
        return {
            ...unit,
            factorA: joined ? row.factorA : null,
            factorB: joined ? row.factorB : null,
            isQualityChecked: joined && row.isQualityChecked === true,
            hasConversion: joined && row.factorA !== null
        };
    });

    return { quantities, units, measureSystems };
}

/**
 * The conversion is linear from the quantity's core unit:
 *   value_in_this_unit = factorA * value_in_core_unit + factorB
 */
export function toCoreValue(unit: UnitWithConversion, value: number): number | null {
    if (unit.factorA === null || unit.factorA === 0) return null;
    return (value - (unit.factorB ?? 0)) / unit.factorA;
}

export function fromCoreValue(unit: UnitWithConversion, coreValue: number): number | null {
    if (unit.factorA === null) return null;
    return unit.factorA * coreValue + (unit.factorB ?? 0);
}

/** Converts a value between two units of the same quantity, via the core unit. */
export function convert(from: UnitWithConversion, to: UnitWithConversion, value: number): number | null {
    const core = toCoreValue(from, value);
    if (core === null) return null;
    return fromCoreValue(to, core);
}

/** The unit part of a `Name|Quantity` identity, for display. */
export function unitNameOf(identity: string | null): string {
    if (!identity) return '';
    const i = identity.indexOf('|');
    return i === -1 ? identity : identity.substring(0, i);
}

/**
 * Parses whatever the value input hands back. Vue's `v-model` casts the value of
 * an `<input type="number">` to a number, but leaves it a string when the field
 * is empty or holds text a number cannot be made of, so both must be handled.
 */
export function parseNumericInput(raw: unknown): number | null {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    const value = Number(trimmed);
    return Number.isFinite(value) ? value : null;
}

/**
 * `AlternateSymbols` holds a comma-separated list of equivalent spellings,
 * e.g. `A/m^2,A.m-2,A*m-2,A/m²`. No symbol in the library contains a comma.
 */
export function parseSymbols(alternateSymbols: string | null): string[] {
    if (!alternateSymbols) return [];
    const seen = new Set<string>();
    for (const part of alternateSymbols.split(',')) {
        const symbol = part.trim();
        if (symbol.length > 0) seen.add(symbol);
    }
    return [...seen];
}

/**
 * Formats a converted value for display. The library stores factors at fifteen
 * significant digits, so that is what is kept; values too large or small to read
 * comfortably as decimals fall back to exponential notation. Trailing zeros are
 * dropped, so exact values stay short.
 */
export function formatNumber(n: number): string {
    if (!Number.isFinite(n)) return '';
    if (n === 0) return '0';
    const rounded = Number(n.toPrecision(15));
    const abs = Math.abs(rounded);
    // Outside this range JavaScript would switch to exponential notation anyway,
    // and the decimal form stops being readable well before it does.
    if (abs >= 1e-4 && abs < 1e15) return String(rounded);
    return rounded.toExponential();
}

/** Human-readable form of the linear transformation, e.g. `[ft] ≡ [m] × 3.28…`. */
export function conversionFormula(unit: UnitWithConversion, coreUnitName: string): string {
    if (!coreUnitName) return '';
    if (coreUnitName === unit.name) return 'core unit';
    if (unit.factorA === null) return '';
    const scale = unit.factorA === 1 ? '' : ` × ${unit.factorA}`;
    const offset = unit.factorB
        ? ` ${unit.factorB > 0 ? '+' : '−'} ${Math.abs(unit.factorB)}`
        : '';
    return `[${unit.name}] ≡ [${coreUnitName}]${scale}${offset}`;
}
